import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GetRequest, PostRequest } from "../../../../api/Request";
import {
  UpdateSubscriptionPayload,
  StoreWithAnalytics,
} from "../storeAnalytics.types";
import {
  SubscriptionTypeEnum,
  SubscriptionPlanEnum,
} from "../../../../enum";
import "../styles/subscriptionUpdateForm.scss";

const SubscriptionUpdateForm: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [store, setStore] = useState<StoreWithAnalytics | null>(null);

  const [formData, setFormData] = useState<UpdateSubscriptionPayload>({
    subscriptionType: SubscriptionTypeEnum.Free,
    plan: SubscriptionPlanEnum.Monthly,
    startDate: new Date().toISOString().slice(0, 16),
    expiryDate: "",
    amount: 0,
    remarks: "",
    categoryId: "687b0bb32ea547814eb1411c", // Default category
    serviceName: "Monthly Subscription",
  });

  useEffect(() => {
    if (storeId) {
      fetchStoreData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const fetchStoreData = async () => {
    if (!storeId) return;

    try {
      const response = await GetRequest(
        `admin/v1/store/${storeId}/analytics`
      );
      setStore(response.data.store);

      // Pre-fill form with existing subscription data
      setFormData({
        subscriptionType: response.data.store?.subscription?.type,
        plan: response.data.store?.subscription?.plan || SubscriptionPlanEnum.Monthly,
        startDate:
          response.data.store?.subscription?.startDate?.slice(0, 16) ||
          new Date().toISOString().slice(0, 16),
        expiryDate: response.data.store?.subscription?.expiryDate?.slice(0, 16) || "",
        amount: response.data.store?.subscription?.amount || 0,
        remarks: response.data.store?.subscription?.remarks || "",
        categoryId: "687b0bb32ea547814eb1411c",
        serviceName: "Monthly Subscription",
      });
    } catch (err: any) {
      setError(err.message || "Failed to fetch store data");
    }
  };

  const handleChange = (
    field: keyof UpdateSubscriptionPayload,
    value: any
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!storeId) return;

    // Validation
    if (!formData.subscriptionType) {
      setError("Subscription type is required");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Convert datetime-local to ISO format
      const payload = {
        ...formData,
        startDate: formData.startDate
          ? new Date(formData.startDate).toISOString()
          : undefined,
        expiryDate: formData.expiryDate
          ? new Date(formData.expiryDate).toISOString()
          : undefined,
      };

      const response = await PostRequest(
        `admin/v1/store/${storeId}/subscription`,
        payload
      );

      setSuccess(response.data.message);

      // Redirect after 2 seconds
      setTimeout(() => {
        navigate(`/store/${storeId}/analytics`);
      }, 2000);
    } catch (err: any) {
      setError(err.message || "Failed to update subscription");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // Calculate default expiry date based on plan
  useEffect(() => {
    if (formData.subscriptionType === SubscriptionTypeEnum.Free) {
      setFormData((prev) => ({ ...prev, amount: 0, expiryDate: "" }));
    } else if (
      formData.plan === SubscriptionPlanEnum.Monthly &&
      formData.startDate
    ) {
      const start = new Date(formData.startDate);
      const expiry = new Date(start);
      expiry.setMonth(expiry.getMonth() + 1);
      setFormData((prev) => ({
        ...prev,
        expiryDate: expiry.toISOString().slice(0, 16),
      }));
    }
  }, [formData.subscriptionType, formData.plan, formData.startDate]);

  return (
    <div className="subscriptionUpdateForm">
      <div className="subscriptionUpdateFormHeader">
        <button className="backButton" onClick={handleCancel}>
          ← Back
        </button>
        <h1>Update Subscription</h1>
        {store && <p>Store: {store.name}</p>}
      </div>

      {error && (
        <div className="subscriptionUpdateFormError">{error}</div>
      )}

      {success && (
        <div className="subscriptionUpdateFormSuccess">{success}</div>
      )}

      <div className="subscriptionUpdateFormCard">
        <form
          className="subscriptionUpdateFormForm"
          onSubmit={handleSubmit}
        >
          <div className="subscriptionUpdateFormFormGroup">
            <label>
              Subscription Type<span className="required">*</span>
            </label>
            <select
              value={formData.subscriptionType}
              onChange={(e) =>
                handleChange(
                  "subscriptionType",
                  e.target.value as SubscriptionTypeEnum
                )
              }
              required
            >
              <option value={SubscriptionTypeEnum.Free}>Free</option>
              <option value={SubscriptionTypeEnum.Paid}>Paid</option>
              <option value={SubscriptionTypeEnum.Trial}>Trial</option>
            </select>
          </div>

          {formData.subscriptionType !== SubscriptionTypeEnum.Free && (
            <>
              <div className="subscriptionUpdateFormFormGroup">
                <label>Subscription Plan</label>
                <select
                  value={formData.plan}
                  onChange={(e) =>
                    handleChange(
                      "plan",
                      e.target.value as SubscriptionPlanEnum
                    )
                  }
                >
                  <option value={SubscriptionPlanEnum.Monthly}>Monthly</option>
                  {/* Add more plans when available */}
                </select>
              </div>

              <div className="subscriptionUpdateFormFormRow">
                <div className="subscriptionUpdateFormFormGroup">
                  <label>Start Date</label>
                  <input
                    type="datetime-local"
                    value={formData.startDate}
                    onChange={(e) => handleChange("startDate", e.target.value)}
                  />
                </div>

                <div className="subscriptionUpdateFormFormGroup">
                  <label>Expiry Date</label>
                  <input
                    type="datetime-local"
                    value={formData.expiryDate}
                    onChange={(e) =>
                      handleChange("expiryDate", e.target.value)
                    }
                  />
                  <span className="helperText">
                    Auto-calculated based on plan
                  </span>
                </div>
              </div>

              <div className="subscriptionUpdateFormFormGroup">
                <label>Amount (₹)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) =>
                    handleChange("amount", parseFloat(e.target.value) || 0)
                  }
                  placeholder="Enter amount in INR"
                />
                <span className="helperText">
                  Amount in Indian Rupees. If amount {">"} 0, an order will be
                  created.
                </span>
              </div>
            </>
          )}

          <div className="subscriptionUpdateFormFormGroup">
            <label>Service Name</label>
            <input
              type="text"
              value={formData.serviceName}
              onChange={(e) => handleChange("serviceName", e.target.value)}
              placeholder="e.g., Monthly Subscription - Premium"
            />
          </div>

          <div className="subscriptionUpdateFormFormGroup">
            <label>Remarks</label>
            <textarea
              value={formData.remarks}
              onChange={(e) => handleChange("remarks", e.target.value)}
              placeholder="Add any notes or remarks about this subscription update"
            />
          </div>

          <div className="subscriptionUpdateFormFormGroup">
            <label>Category ID</label>
            <input
              type="text"
              value={formData.categoryId}
              onChange={(e) => handleChange("categoryId", e.target.value)}
              placeholder="Default: 687b0bb32ea547814eb1411c"
            />
            <span className="helperText">
              Category for order creation (optional)
            </span>
          </div>

          <div className="subscriptionUpdateFormFormGroup">
            <label>Subcategory ID</label>
            <input
              type="text"
              value={formData.subCategoryId || ""}
              onChange={(e) => handleChange("subCategoryId", e.target.value)}
              placeholder="Optional"
            />
          </div>

          <div className="subscriptionUpdateFormActions">
            <button
              type="button"
              className="cancelBtn"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submitBtn"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Subscription"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscriptionUpdateForm;
